import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang, usePath } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ColourSpectrum = () => {
  const { t } = useLang();
  const lp = usePath();
  const S = t.spectrum;
  const P = t.products;
  const [products, setProducts] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  if (products.length === 0) return null;
  const p = products[idx];

  return (
    <section data-testid="colour-spectrum" className="relative bg-clara-espresso text-clara-cream py-24 lg:py-32 noise-overlay border-t border-clara-cream/10">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow text-clara-terracotta mb-6">{S.eyebrow}</p>
          <h2 className="font-display font-light text-4xl lg:text-6xl leading-[0.98] tracking-tighter max-w-2xl mb-14">{S.title}</h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Link to={lp(`/grades/${p.code}`)} data-testid="spectrum-image-link" className="relative block overflow-hidden border border-clara-cream/15 aspect-[4/3]" style={{ backgroundColor: p.hex }}>
            <AnimatePresence mode="wait">
              <motion.img
                key={p.code}
                src={p.image}
                alt={`Clara ${p.code} cocoa powder`}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute bottom-4 left-4 bg-clara-espresso/80 backdrop-blur-sm px-4 py-2 font-data text-xs tracking-[0.15em] text-clara-cream/90">
              {idx + 1} / {products.length}
            </div>
          </Link>

          <div>
            <AnimatePresence mode="wait">
              <motion.div key={p.code} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
                <div className="font-display font-light text-6xl lg:text-7xl tracking-tighter" data-testid="spectrum-grade-code">{p.code}</div>
                <div className="text-clara-darkmuted mt-2 mb-10">{P.colours[p.color]} · pH {p.ph} · {P.types[p.type]}</div>
              </motion.div>
            </AnimatePresence>

            <input
              type="range"
              min={0}
              max={products.length - 1}
              step={1}
              value={idx}
              onChange={(e) => setIdx(Number(e.target.value))}
              data-testid="spectrum-slider"
              aria-label={S.eyebrow}
              className="w-full accent-clara-terracotta h-1.5 cursor-pointer"
            />
            <div className="flex justify-between mt-4">
              {products.map((g, i) => (
                <button
                  key={g.code}
                  onClick={() => setIdx(i)}
                  data-testid={`spectrum-tick-${g.code}`}
                  aria-label={g.code}
                  className={`w-3 h-3 rounded-full border transition-colors ${i === idx ? "bg-clara-terracotta border-clara-terracotta" : "border-clara-cream/40 hover:border-clara-cream"}`}
                />
              ))}
            </div>

            <Link to={lp(`/grades/${p.code}`)} data-testid="spectrum-view-grade" className="mt-10 inline-flex items-center gap-2 rounded-full border border-clara-cream/30 px-7 py-3.5 text-sm font-semibold text-clara-cream hover:border-clara-terracotta hover:text-clara-terracotta transition-colors duration-300">
              {S.viewGrade} {p.code} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
